/* ==========================================================================
   GOODS SCENE -- PDP SHARE / COPY LINK

   🛑 THIS IS A PROXY, NOT A REPLACEMENT. Dawn's <share-button> component is the
   authoritative source and is left completely intact: assets/share.js and
   snippets/share-button.liquid are never touched, no Dawn node is moved,
   rewritten or removed, and no attribute of theirs is altered. This file only
   APPENDS two controls and forwards their clicks into capabilities Dawn already
   exposes.

   🔑 WHY A PROXY IS NEEDED AT ALL. share.js wires exactly ONE of two branches:

       if (navigator.share)  -> hides <details>, wires the plain button only
       else                  -> wires the <details> toggle and .share-button__copy

   So in any given browser one of the two controls the design asks for is dead.
   On Chrome/Safari desktop the copy button has no listener; on Firefox the
   share button has none. No amount of CSS can revive either. This file gives
   each control its own listener so both work in both branches.

   🛑 THERE IS ONE PRODUCT URL AND IT IS DAWN'S. Every action reads
   input.value -- the field share-button.liquid renders from {{ share_link }},
   and the same value share.js itself uses. No second URL source is invented,
   and document.location is never consulted.

   ⚠️ NO CUSTOMER-VISIBLE STRING IS WRITTEN HERE. The share label is read from
   Dawn's own button (the block's share_label setting) and the copy label from
   the translated .visually-hidden span already inside .share-button__copy
   ('general.share.copy_to_clipboard'). The copied confirmation reuses
   window.accessibilityStrings.shareSuccess. If any of them is missing the
   feature declines rather than inventing English.

   ⚠️ FAILS OPEN AT EVERY STEP. Missing input, missing button, missing label ->
   return, and the page keeps Dawn's stock share UI. The stylesheet is gated on
   the data-gs-share attribute this file sets, so nothing is restyled until the
   proxy is actually in place.
   ========================================================================== */

(function () {
  /* 🔑 A BUILT CONTROL, NOT A CLONED ONE. Only the ICON is cloned from Dawn --
     cloneNode leaves the original in the DOM untouched -- while the button
     itself is ours, so Dawn's own classes, ids and handlers cannot be
     duplicated onto a second node. */
  function buildControl(modifier, icon, text) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'gs-share__btn gs-share__btn--' + modifier;

    var iconWrap = document.createElement('span');
    iconWrap.className = 'gs-share__icon';
    iconWrap.setAttribute('aria-hidden', 'true');
    if (icon) iconWrap.appendChild(icon.cloneNode(true));

    var label = document.createElement('span');
    label.className = 'gs-share__label';
    label.textContent = text;

    button.appendChild(iconWrap);
    button.appendChild(label);

    return { button: button, label: label };
  }

  function enhance(el) {
    if (el.getAttribute('data-gs-share') === 'true') return;

    /* ⚠️ THE QUICK ADD MODAL INJECTS THIS WHOLE SECTION. Dawn already hides the
       share block there with .quick-add-hidden; this keeps the proxy out of it
       entirely rather than relying on that class. */
    if (typeof el.closest === 'function' && el.closest('quick-add-modal')) return;

    var input = el.querySelector('input');
    var nativeButton = el.querySelector('.share-button__button');
    var details = el.querySelector('details');
    var copyButton = el.querySelector('.share-button__copy');
    if (!input || !nativeButton || !details || !copyButton) return;

    var shareText = (nativeButton.textContent || '').trim();
    var copyTextNode = copyButton.querySelector('.visually-hidden');
    var copyText = copyTextNode ? (copyTextNode.textContent || '').trim() : '';
    if (!shareText || !copyText) return;

    var share = buildControl('share', nativeButton.querySelector('svg'), shareText);
    var copy = buildControl('copy', copyButton.querySelector('svg'), copyText);

    /* 🛑 SHARE KEEPS DAWN'S OWN TWO BEHAVIOURS, IN DAWN'S OWN ORDER. Native
       Web Share where the browser has it; otherwise open Dawn's fallback panel,
       which is the safe route it already ships. Setting .open directly rather
       than clicking the summary means it cannot depend on a hidden element
       receiving a synthetic click. No social network is ever hard-coded. */
    share.button.addEventListener('click', function () {
      if (navigator.share) {
        navigator.share({ url: input.value, title: document.title });
        return;
      }

      details.open = true;
    });

    /* 🔑 COPY IS INDEPENDENT OF WHICH BRANCH share.js TOOK. Same clipboard API,
       same URL, and the confirmation is Dawn's already-translated success
       string swapped into the label for a moment.

       ⚠️ THE TIMER IS FOR RESTORING A LABEL, NOTHING ELSE -- nothing here polls,
       waits for, or retries anything. The guard stops a rapid second click from
       capturing the confirmation text as the label to restore. */
    copy.button.addEventListener('click', function () {
      if (!navigator.clipboard || !navigator.clipboard.writeText) return;

      navigator.clipboard.writeText(input.value).then(function () {
        var done = window.accessibilityStrings && window.accessibilityStrings.shareSuccess;
        if (!done) return;
        if (copy.button.getAttribute('data-gs-copied') === 'true') return;

        copy.button.setAttribute('data-gs-copied', 'true');
        copy.label.textContent = done;

        setTimeout(function () {
          copy.label.textContent = copyText;
          copy.button.removeAttribute('data-gs-copied');
        }, 2400);
      });
    });

    var row = document.createElement('div');
    row.className = 'gs-share';
    row.appendChild(share.button);
    row.appendChild(copy.button);

    /* ⚠️ APPENDED INSIDE <share-button>, NEVER ELSEWHERE. The block already sits
       where the design wants it -- last in the purchase column, under More
       payment options -- so no DOM is relocated to position this row. */
    el.appendChild(row);
    el.setAttribute('data-gs-share', 'true');
  }

  function init() {
    var nodes = document.querySelectorAll('share-button');

    for (var i = 0; i < nodes.length; i++) {
      enhance(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
